import React from "react";
import Image from "next/image";
import Link from "next/link";
import { MapPin, ShieldCheck, Maximize, ArrowRight, Clock, Navigation, Star } from "lucide-react";

interface ProjectCardProps {
  project: any;
}

export function ProjectCard({ project }: ProjectCardProps) {
  // Helper to determine status icon
  const getStatusIcon = (status: string) => {
    if (!status) return null;
    const s = status.toLowerCase();
    if (s.includes('limited')) return <Clock className="mr-1.5 h-3.5 w-3.5 shrink-0 text-emerald-800" />;
    if (s.includes('visit')) return <MapPin className="mr-1.5 h-3.5 w-3.5 shrink-0 text-emerald-800" />;
    return <Navigation className="mr-1.5 h-3.5 w-3.5 shrink-0 text-emerald-800" />;
  };

  return (
    <div className="group flex h-full flex-col overflow-hidden rounded-xl bg-white shadow-sm transition-all duration-300 hover:shadow-xl hover:-translate-y-1 border border-[var(--border)]">
      {/* Hero Image Section */}
      <div className="relative h-[240px] w-full overflow-hidden bg-[var(--page-soft)]">
        {project.hero ? (
          <Image
            src={project.hero}
            alt={project.name}
            fill
            sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
            className="object-cover transition-transform duration-700 group-hover:scale-105"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-[var(--page)] text-[var(--muted)]">
            <span className="text-xs uppercase tracking-widest opacity-50">No Image</span>
          </div>
        )}
        
        {/* Top Left Status Badge */}
        {project.status && (
          <div className="absolute top-4 left-4 flex items-center rounded-full bg-white/95 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-emerald-900 shadow-sm backdrop-blur-sm">
            {getStatusIcon(project.status)}
            {project.status}
          </div>
        )}
      </div>

      {/* Content Section */}
      <div className="flex flex-1 flex-col p-6">
        {/* Header: Title, Location, and Logo */}
        <div className="mb-4 flex items-start justify-between gap-4">
          <div className="flex-1">
            <h3 className="font-serif text-[1.35rem] font-medium text-[var(--ink)] mb-2">{project.name}</h3>
            <div className="flex items-center text-xs font-semibold text-[var(--muted)]">
              <MapPin className="mr-1.5 h-4 w-4 shrink-0 text-[var(--muted)]" />
              <span className="line-clamp-1">{project.location}</span>
            </div>
          </div>
          {project.logo && (
            <div className="relative h-10 w-24 shrink-0">
              <Image
                src={project.logo}
                alt={`${project.name} logo`}
                fill
                sizes="96px"
                className="object-contain object-right"
              />
            </div>
          )}
        </div>

        {/* Divider */}
        <div className="my-5 h-px w-full bg-[var(--border)]" />

        {/* Description / Summary */}
        <p className="mb-6 flex-1 text-sm leading-relaxed text-[var(--muted)]">
          {project.summary || project.description}
        </p>

        {/* Feature Badges */}
        <div className="mb-6 flex flex-col gap-2.5">
          {project.approval && (
            <div className="flex w-fit items-center rounded-md bg-[#f4f6f5] px-3 py-2 text-xs font-bold text-[var(--ink)]">
              <ShieldCheck className="mr-2 h-4 w-4 shrink-0 text-[var(--ink)]" />
              {project.approval}
            </div>
          )}
          {project.acreage && (
            <div className="flex w-fit items-center rounded-md bg-[#f4f6f5] px-3 py-2 text-xs font-bold text-[var(--ink)]">
              <Maximize className="mr-2 h-4 w-4 shrink-0 text-[var(--ink)]" />
              {project.acreage}
            </div>
          )}
          {!project.approval && !project.acreage && project.facts && project.facts.length > 0 && (
             <div className="flex w-fit items-center rounded-md bg-[#f4f6f5] px-3 py-2 text-xs font-bold text-[var(--ink)]">
                <Star className="mr-2 h-4 w-4 shrink-0 text-[var(--ink)]" />
                {project.facts[0][1]}
             </div>
          )}
        </div>

        {/* Footer Link Divider */}
        <div className="mb-5 h-px w-full bg-[var(--border)]" />

        {/* Footer Link */}
        <Link
          href={`/projects/${project.slug}`}
          className="group/btn flex items-center justify-between rounded-lg bg-transparent py-1 text-sm font-bold text-[var(--ink)] transition-colors"
        >
          <span>View project</span>
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[#203126] text-white transition-transform duration-300 group-hover/btn:scale-110 shadow-sm">
            <ArrowRight className="h-4 w-4" />
          </div>
        </Link>
      </div>
    </div>
  );
}
